import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { format } from "date-fns";
import { LABELS } from "../../platform/config";
import { Visit } from "../../services/clientRecordsService";

const PAGE = 10;

type Props = {
  /** Upcoming (soonest first) then past (latest first), as the record lists them. */
  visits: Visit[];
  value: string;
  onChange: (visitId: string) => void;
  disabled?: boolean;
};

const describe = (v: Visit) =>
  `${format(new Date(v.startAt), "EEE d MMM yyyy, HH:mm")}${v.service ? ` · ${v.service.name}` : ""}`;

/**
 * Picks the visit a file belongs to, ten at a time, so a long history does
 * not turn into one endless list. Opens on the page holding the current pick.
 */
const VisitPicker: React.FC<Props> = ({ visits, value, onChange, disabled }) => {
  const anchor = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const now = Date.now();
  const pages = Math.max(1, Math.ceil(visits.length / PAGE));
  const selected = visits.find((v) => v.id === value);

  const shown = useMemo(() => visits.slice(page * PAGE, page * PAGE + PAGE), [visits, page]);

  const show = () => {
    if (disabled) return;
    const at = visits.findIndex((v) => v.id === value);
    setPage(at >= 0 ? Math.floor(at / PAGE) : 0);
    setOpen(true);
  };

  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  // A heading wherever the page moves from upcoming to past (or starts).
  const items: React.ReactNode[] = [];
  shown.forEach((v, i) => {
    const upcoming = new Date(v.startAt).getTime() >= now;
    const prev = i > 0 ? new Date(shown[i - 1].startAt).getTime() >= now : null;
    if (prev === null || prev !== upcoming) {
      items.push(
        <ListSubheader key={`h-${v.id}`} sx={{ lineHeight: "30px", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {upcoming ? "Upcoming" : "Past"}
        </ListSubheader>
      );
    }
    items.push(
      <MenuItem key={v.id} selected={v.id === value} onClick={() => pick(v.id)} sx={{ fontSize: "0.9rem" }}>
        {describe(v)}
      </MenuItem>
    );
  });

  return (
    <>
      <TextField
        ref={anchor}
        size="small"
        fullWidth
        label={`${LABELS.visit} (optional)`}
        InputLabelProps={{ shrink: true }}
        value={selected ? describe(selected) : ""}
        placeholder={`Not tied to a ${LABELS.visit.toLowerCase()}`}
        onClick={show}
        onKeyDown={(e) => (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ") && (e.preventDefault(), show())}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          sx: { cursor: "pointer", "& input": { cursor: "pointer" } },
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon sx={{ color: "action.active", transform: open ? "rotate(180deg)" : "none" }} />
            </InputAdornment>
          ),
        }}
        inputProps={{ "aria-haspopup": "listbox", "aria-expanded": open }}
      />
      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: anchor.current?.offsetWidth, maxWidth: "calc(100vw - 32px)", mt: 0.5, borderRadius: 2 } } }}
      >
        <MenuList dense autoFocusItem sx={{ py: 0.5 }}>
          <MenuItem selected={!value} onClick={() => pick("")} sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
            Not tied to a {LABELS.visit.toLowerCase()}
          </MenuItem>
          {items}
        </MenuList>
        {pages > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.5, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", flexGrow: 1 }}>
              {page * PAGE + 1}–{Math.min(visits.length, page * PAGE + PAGE)} of {visits.length}
            </Typography>
            <IconButton size="small" aria-label="Previous page" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontSize: "0.78rem" }}>
              {page + 1} / {pages}
            </Typography>
            <IconButton size="small" aria-label="Next page" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default VisitPicker;
